import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import UserAddressHistory from "../components/user-profile/UserAddressHistory";
import UserDelete from "../components/user-profile/UserDelete";
import UserChangePassword from "../components/user-profile/UserChangePassword";
import { useDisclosure } from "@mantine/hooks";
import CollectionCreatingModal from "../components/CollectionCreatingModal";
import { useForm } from "@mantine/form";
import { Flex, Box, useMantineTheme, Title, Text, Tabs } from "@mantine/core";

import UserInfo from "../components/user-profile/UserInfo";
import Billing from "../components/user-profile/Billing";
import AccountSettings from "../components/user-profile/AccountSettings";
import UsageStatistics from "../components/user-profile/UsageStatistics";
import CollectionNames from "../components/user-profile/CollectionNames";
import Folders from "../components/user-profile/Folders";
import useUserTimeZoneDataStorage from "../utils/useUserTimeZoneDataStorage";
import Providers from "../components/user-profile/Providers";
import {
  validateCollectionDescription,
  validateCollectionName,
} from "../utils/validators";
import { parseProfileHash } from "../utils/profileHash";

const UserProfile = ({ staticBasePath, djangoContext }) => {
  // temporarily hide Providers tab
  const hideProviders = false;

  const theme = useMantineTheme();

  // Memoize the callback function to avoid unnecessary re-renders
  const handleTimeZone = useCallback((timeZone) => {
    setTimeZone(timeZone);
  }, []);

  const { dataStorage, timeZone, setTimeZone, setDataStorage } =
    useUserTimeZoneDataStorage();

  // Memoize the callback function to avoid unnecessary re-renders
  const handleDataStorage = useCallback((dataStorage) => {
    setDataStorage(dataStorage);
  }, []);

  const [changedUserName, setChangedUserName] = useState(false);

  const [userAddressHistoryData, setUserAddressHistoryData] = useState("");

  const [userDelete, setUserDelete] = useState(false);

  const [changePassword, setChangePassword] = useState(false);

  // Update user Quota after creating Collection (setter passed to modal; value not read here)
  const [, setUpdateStampQuota] = useState(false);

  // Memoize the callback function to avoid unnecessary re-renders
  const handleUpdateStampQuota = useCallback((date) => {
    setUpdateStampQuota(date);
  }, []);

  // Show confirmation create Collection
  const [
    showConfirmationCollectionCreate,
    setShowConfirmationCollectionCreate,
  ] = useState(false);

  // Memoize the callback function to avoid unnecessary re-renders
  const handleShowConfirmationCollectionCreate = useCallback((isDisabled) => {
    setShowConfirmationCollectionCreate(isDisabled);
  }, []);

  //State for storing the name
  const [nameCollection, setNameCollection] = useState("");

  // Memoize the callback function to avoid unnecessary re-renders
  const handleNameCollection = useCallback((collection) => {
    setNameCollection(collection);
  }, []);

  //Error adding name
  const [nameCollectionError, setNameCollectionError] = useState(false);

  // Memoize the callback function to avoid unnecessary re-renders
  const handleNameCollectionError = useCallback((collectionError) => {
    setNameCollectionError(collectionError);
  }, []);

  //Successfully added name
  const [nameCollectionSuccess, setNameCollectionSuccess] = useState("");

  // Memoize the callback function to avoid unnecessary re-renders
  const handleNameCollectionSuccess = useCallback((collectionSuccess) => {
    setNameCollectionSuccess(collectionSuccess);
  }, []);

  const [opened, { open, close }] = useDisclosure(false);

  const form = useForm({
    mode: "uncontrolled",
    collectionName: "",
    collectionDescription: "",
    // functions will be used to validate values at corresponding key
    validate: {
      collectionName: (value) => {
        return validateCollectionName(value);
      },
      collectionDescription: (value) => {
        return validateCollectionDescription(value);
      },
    },
  });

  const [profileHash, setProfileHash] = useState(() =>
    parseProfileHash(window.location.hash),
  );
  const { tab: activeTab, refreshCredits } = profileHash;

  useEffect(() => {
    // Handler for hash change events
    const handleHashChange = () => {
      setProfileHash(parseProfileHash(window.location.hash));
    };

    // Listen for changes to the URL hash
    window.addEventListener("hashchange", handleHashChange);

    // Clean up the event listener when the component is unmounted
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // Function to handle tab change
  const handleTabChange = useCallback((value) => {
    setProfileHash({ tab: value, refreshCredits: false });
    window.location.hash = value;
  }, []);
  return (
    <>
      <title>vBase User Profile</title>
      <meta name="description" content="User Profile" />
      <meta property="og:title" content="vBase User Profile" />
      <meta property="og:description" content="User Profile" />

      <CollectionCreatingModal
        titleLoading="One moment! Creating new Collection in progress..."
        textLoading="Can take 20 seconds, but usually faster!"
        titleConfirmation="Confirm New Collection"
        textConfirmation="This Collection will be permanently associated with your Blockchain Address. This operation costs 1 stamp credit."
        nameCollection={nameCollection}
        onNameCollection={handleNameCollection}
        onNameCollectionSuccess={handleNameCollectionSuccess}
        onNameCollectionError={handleNameCollectionError}
        onShowConfirmationCollectionCreate={
          handleShowConfirmationCollectionCreate
        }
        onUpdateStampQuota={handleUpdateStampQuota}
        opened={opened}
        close={close}
        form={form}
      />

      <Flex
        justify={theme.flex.justify.center}
        styles={() => ({
          root: {
            paddingTop: "6em",
            paddingBottom: "2em",
          },
        })}
      >
        <Title size="3em" order={1}>
          User Profile
        </Title>
      </Flex>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        color={theme.customColors.black}
        defaultValue="user_info"
        keepMounted={false}
        styles={() => ({
          root: {
            width: "100%",
          },
          tabLabel: {
            fontSize: "1.3em",
          },
        })}
      >
        <Tabs.List justify={theme.flex.justify.center}>
          <Tabs.Tab value="user_info">
            <Text
              styles={() => ({
                root: {
                  opacity: activeTab === "user_info" ? "1" : "0.5",
                },
              })}
            >
              User Info
            </Text>
          </Tabs.Tab>
          <Tabs.Tab value="billing">
            <Text
              styles={() => ({
                root: {
                  opacity: activeTab === "billing" ? "1" : "0.5",
                },
              })}
            >
              Billing
            </Text>
          </Tabs.Tab>
          <Tabs.Tab value="account_settings">
            <Text
              styles={() => ({
                root: {
                  opacity: activeTab === "account_settings" ? "1" : "0.5",
                },
              })}
            >
              Account Settings
            </Text>
          </Tabs.Tab>
          <Tabs.Tab value="usage_statistics">
            <Text
              styles={() => ({
                root: {
                  opacity: activeTab === "usage_statistics" ? "1" : "0.5",
                },
              })}
            >
              Usage Statistics
            </Text>
          </Tabs.Tab>
          <Tabs.Tab value="collections">
            <Text
              styles={() => ({
                root: {
                  opacity: activeTab === "collections" ? "1" : "0.5",
                },
              })}
            >
              Collections
            </Text>
          </Tabs.Tab>
          <Tabs.Tab value="user_data_storage">
            <Text
              styles={() => ({
                root: {
                  opacity: activeTab === "user_data_storage" ? "1" : "0.5",
                },
              })}
            >
              Stamped File Storage
            </Text>
          </Tabs.Tab>
          {!hideProviders && (
            <Tabs.Tab value="providers">
              <Text
                styles={() => ({
                  root: {
                    opacity: activeTab === "providers" ? "1" : "0.5",
                  },
                })}
              >
                Broker Integrations
              </Text>
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Box
          style={(theme) => ({
            backgroundColor: theme.customBackgrounds.userProfileTabContent,
          })}
        >
          <Box
            style={() => ({
              maxWidth: "60rem",
              marginLeft: "auto",
              marginRight: "auto",
              width: "calc(100% - 2em)",
            })}
          >
            <Tabs.Panel value="user_info">
              {timeZone ? (
                <UserInfo
                  setUserAddressHistoryData={setUserAddressHistoryData}
                  setUserDelete={setUserDelete}
                  setChangePassword={setChangePassword}
                  setChangedUserName={setChangedUserName}
                  changedUserName={changedUserName}
                  timeZone={timeZone}
                />
              ) : (
                ""
              )}
            </Tabs.Panel>

            <Tabs.Panel value="billing">
              <Billing timeZone={timeZone} refreshCredits={refreshCredits} />
            </Tabs.Panel>

            <Tabs.Panel value="account_settings">
              <AccountSettings
                onTimeZone={handleTimeZone}
                timeZone={timeZone}
                onDataStorage={handleDataStorage}
                dataStorage={dataStorage}
              />
            </Tabs.Panel>
            <Tabs.Panel value="usage_statistics">
              {timeZone ? <UsageStatistics timeZone={timeZone} /> : ""}
            </Tabs.Panel>
            <Tabs.Panel value="collections">
              <CollectionNames
                onUpdateStampQuota={handleUpdateStampQuota}
                onShowConfirmationCollectionCreate={
                  handleShowConfirmationCollectionCreate
                }
                showConfirmationCollectionCreate={
                  showConfirmationCollectionCreate
                }
                onNameCollection={handleNameCollection}
                nameCollection={nameCollection}
                nameCollectionSuccess={nameCollectionSuccess}
                onNameCollectionError={handleNameCollectionError}
                nameCollectionError={nameCollectionError}
                open={open}
                form={form}
                djangoContext={djangoContext}
              />
            </Tabs.Panel>
            <Tabs.Panel value="user_data_storage">
              <Folders timeZone={timeZone} dataStorage={dataStorage} />
            </Tabs.Panel>
            {!hideProviders && (
              <Tabs.Panel value="providers">
                <Providers />
              </Tabs.Panel>
            )}
          </Box>
        </Box>
      </Tabs>

      {userAddressHistoryData ? (
        <UserAddressHistory
          userAddressHistoryData={userAddressHistoryData}
          setUserAddressHistoryData={setUserAddressHistoryData}
          timeZone={timeZone}
        />
      ) : (
        ""
      )}

      {userDelete ? (
        <UserDelete
          staticBasePath={staticBasePath}
          setUserDelete={setUserDelete}
        />
      ) : (
        false
      )}

      {changePassword ? (
        <UserChangePassword
          staticBasePath={staticBasePath}
          setChangePassword={setChangePassword}
        />
      ) : (
        false
      )}
    </>
  );
};
UserProfile.propTypes = {
  staticBasePath: PropTypes.string.isRequired,
  djangoContext: PropTypes.object.isRequired,
  onUpdateStampQuota: PropTypes.bool,
  showConfirmationCollectionCreate: PropTypes.func,
  handleNameCollection: PropTypes.func,
};

export default UserProfile;
